import type { SupWeb3SelectedRoute } from "@/components/Web3/Common/Route/State";
import type { ResponseTxFees } from "@/hooks/tx-flow/useDepositStart";
import type { SupTransactionType, SupWeb3TxSingleVault } from "@/types";
import React, { useMemo } from "react";

import Image from "next/image";
import config from "@/../config/dapp.config";
import dictionaryConfig from "@/../config/dictionary.config";
import { FixedNumber } from "@ethersproject/bignumber";

import useRewardSettings from "@/hooks/data/useRewardSettings";
import UiTooltip from "@/components/Ui/Tooltip";
import Web3TxSettingsHandler from "@/components/Web3/TxSettings/Handler";
import { SupHeading, SupText } from "@repo/core-ui";
import SupLoading from "@/components/Ui/Loading";
import UiLabelApy from "@/components/Ui/Label/Apy";
import { classNames, getNetVaultApy, getVaultApy } from "@/utils";

interface Web3CommonSummaryProps {
  isDeposit?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  vaultList: SupWeb3TxSingleVault[];
  totalGasCost?: number;
  type?: SupTransactionType;
  discountedAmount?: ResponseTxFees;
  selectedRoutes: SupWeb3SelectedRoute[];
}

export default function Web3CommonSummary({
  vaultList,
  totalGasCost,
  isDisabled = false,
  isLoading = true,
  type = "deposit",
  discountedAmount,
  selectedRoutes,
}: Web3CommonSummaryProps) {
  const rewards = useRewardSettings();

  // Getting total amount depositing in USD
  const amountUsd = useMemo(() => {
    return vaultList.reduce((acc, singleVault) => acc.addUnsafe(singleVault.amountInFiat || FixedNumber.from(0)), FixedNumber.from(0));
  }, [vaultList]);

  // Calculating average APY for those vaults that have it (if no vaults have it — don't show it)
  const averageApy = useMemo(() => {
    const filteredKnownApyVaults = vaultList.filter(singleVault => getVaultApy(singleVault) !== dictionaryConfig.statsDefaultValue);
    if (filteredKnownApyVaults.length === 0) {
      return undefined;
    }

    return (
      filteredKnownApyVaults.reduce((acc, singleVault) => {
        const rawApy = getNetVaultApy(singleVault, rewards, undefined, true);
        return acc + (rawApy !== dictionaryConfig.statsDefaultValue ? rawApy : 0);
      }, 0) / filteredKnownApyVaults.length
    );
  }, [rewards, vaultList]);

  const receivingAmount = useMemo(() => {
    return selectedRoutes.reduce(
      (acc, singleSelectedRoute) => acc - (typeof singleSelectedRoute.selectedRoute === "object" ? singleSelectedRoute.selectedRoute.toAmountInUsd : 0) * -1,
      0
    );
  }, [selectedRoutes]);

  const isDirect = useMemo(() => selectedRoutes.some(singleSelectedRoute => singleSelectedRoute.selectedRoute === "direct"), [selectedRoutes]);

  const totalGasCostFinal = useMemo(() => {
    if (isLoading) {
      return undefined;
    }
    return (totalGasCost ?? 0) + (discountedAmount?.original_fee_usd ? discountedAmount.original_fee_usd - discountedAmount.discount_usd : 0);
  }, [discountedAmount?.discount_usd, discountedAmount?.original_fee_usd, isLoading, totalGasCost]);

  const previousTotalGasCost = useMemo(() => {
    return !isLoading && discountedAmount?.original_fee_usd ? discountedAmount.original_fee_usd : undefined;
  }, [discountedAmount?.original_fee_usd, isLoading]);

  const discountAmount = useMemo(() => discountedAmount?.discount_percent, [discountedAmount?.discount_percent]);

  return (
    <div className={classNames("inline-flex w-full flex-col items-start justify-start", isDisabled && "text-neutral-500")}>
      <div className="mb-1 flex h-10 w-full items-center justify-between">
        <SupText variant="subtitle1" family="mono">
          {type.toUpperCase()} SUMMARY
        </SupText>
        <Web3TxSettingsHandler />
      </div>
      <div className="flex flex-col items-start justify-start self-stretch">
        <div className="inline-flex h-5 items-center justify-between self-stretch">
          <SupText variant="caption" family="mono">
            AVERAGE APY
          </SupText>
          <SupText as="div" variant="caption" family="mono">
            <UiLabelApy value={averageApy} unwrapped />
          </SupText>
        </div>

        <div className="inline-flex h-5 items-center justify-between self-stretch">
          <SupText variant="caption" family="mono">
            Estimated Fees
          </SupText>
          <SupText variant="caption" family="mono" className="flex select-none items-center space-x-2" as="div">
            {discountAmount && (
              <UiTooltip content="SPONSORED BY SUPERFORM">
                <SupText
                  variant="caption"
                  weight="bold"
                  as="div"
                  className="flex h-5 cursor-help items-center space-x-1 rounded-full border-[.5px] border-[#62DFFF] bg-[#62DFFF]/30 px-1.5 py-0.5 text-[#62DFFF]"
                >
                  <Image
                    src="https://static.debank.com/image/chain/logo_url/linea/32d4ff2cf92c766ace975559c232179c.png"
                    className="overflow-hidden rounded-full"
                    width={14}
                    height={14}
                    alt="linea"
                  />
                  <span>{parseFloat(String(discountAmount)).toFixed(config.decimalsRoundingLimit)}% OFF</span>
                </SupText>
              </UiTooltip>
            )}
            {discountAmount && previousTotalGasCost && (
              <div className="inline-flex line-through">${parseFloat(String(previousTotalGasCost)).toFixed(config.decimalsRoundingLimit)}</div>
            )}
            <div>{!totalGasCostFinal ? "--" : `$${parseFloat(String(totalGasCostFinal)).toFixed(config.decimalsRoundingLimit)}`}</div>
          </SupText>
        </div>
        <div className="inline-flex h-5 items-center justify-between self-stretch">
          <SupText variant="caption" family="mono">
            receiving TODAY
          </SupText>
          {!isLoading ? (
            <SupHeading variant="h6" family="mono" weight="bold">
              $
              {isDirect
                ? amountUsd.round(config.decimalsRoundingLimit).toUnsafeFloat().toFixed(config.decimalsRoundingLimit)
                : parseFloat(String(receivingAmount)).toFixed(config.decimalsRoundingLimit)}
            </SupHeading>
          ) : (
            <div className="wrappedLoader h-4 w-4">
              <SupLoading />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
